import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { InformationCircleIcon } from '@gto/ui/icons/outline'
import { Typography } from '@gto/ui/typography'

import { ArrowRightIcon, IconEdgeCoin } from '@/assets/icons'
import { sendGaEvent } from '@/gtag'
import { useAuth } from '@/hooks/use-auth'

export const MainSection = () => {
  const { isAuthorized } = useAuth()

  return (
    <section
      className="flex flex-col items-center gap-16 sm:gap-12"
      style={{ contain: 'layout', willChange: 'auto' }}
    >
      <div className="text-center">
        <Typography
          gutterBottom
          component="h1"
          variant="h3"
        >
          <FormattedMessage id="home.mainSection.title" />
        </Typography>

        <Typography
          color="text.secondary"
          component="h2"
          variant="h6"
        >
          <FormattedMessage id="home.mainSection.description" />
        </Typography>
      </div>
      <div className="flex w-full max-w-[80%] flex-row justify-between gap-8 overflow-hidden rounded-[120px] bg-gradient-primary-vertical px-8 py-6 md:w-auto md:max-w-none md:flex-col md:rounded-lg">
        <Step
          stepNumber={1}
          title={<FormattedMessage id="home.mainSection.steps.1.title" />}
        />
        <div className="flex-[0_0_auto] md:hidden">
          <ArrowRightIcon />
        </div>
        <Step
          stepNumber={2}
          title={
            <FormattedMessage
              id="home.mainSection.steps.2.title"
              values={{
                coin: () => (
                  <IconEdgeCoin style={{ display: 'inline-block', verticalAlign: 'bottom' }} />
                ),
              }}
            />
          }
        />
        <div className="flex-[0_0_auto] md:hidden">
          <ArrowRightIcon />
        </div>
        <Step
          stepNumber={3}
          title={
            <FormattedMessage
              id="home.mainSection.steps.3.title"
              values={{
                coin: () => (
                  <IconEdgeCoin style={{ display: 'inline-block', verticalAlign: 'bottom' }} />
                ),
              }}
            />
          }
        />
      </div>
      <div className="flex flex-col items-center gap-8">
        <div className="flex w-full items-center justify-center gap-4">
          {isAuthorized ? (
            <Button
              component={Link}
              size="large"
              to="/app/deals"
              variant="contained"
              onClick={() => {
                sendGaEvent({
                  event: 'home_top_view_deals',
                })
              }}
            >
              <FormattedMessage id="common.controls.seeAllDeals" />
            </Button>
          ) : (
            <>
              <Button
                as={Link}
                href="/signup"
                size="large"
                variant="contained"
                onClick={() => {
                  sendGaEvent({
                    event: 'home_top_create_account',
                  })
                }}
              >
                <FormattedMessage id="common.controls.createAccount" />
              </Button>

              <Button
                as={Link}
                color="secondary"
                href="/login"
                size="large"
                variant="contained"
                onClick={() => {
                  sendGaEvent({
                    event: 'home_top_login',
                  })
                }}
              >
                <FormattedMessage id="common.controls.login" />
              </Button>
            </>
          )}
        </div>

        <Typography
          className="flex w-full items-center gap-1 text-center font-medium text-text-secondary"
          variant="body2"
        >
          <InformationCircleIcon
            color="inherit"
            height={20}
            style={{ verticalAlign: 'bottom' }}
          />{' '}
          <span>
            <FormattedMessage id="home.mainSection.caption" />
          </span>
        </Typography>
      </div>
    </section>
  )
}

type StepProps = { stepNumber: number; title: string | ReactNode }

const Step = ({ stepNumber, title }: StepProps) => {
  return (
    <div className="flex max-w-[28%] flex-[0_0_auto] md:max-w-full">
      <div className="flex items-center gap-4">
        <div className="flex size-[60px] flex-[0_0_auto] items-center justify-center rounded-full border border-primary-main md:size-10">
          <Typography className="font-medium">{stepNumber}</Typography>
        </div>
        <Typography
          className="font-semibold leading-[1.2] sm:text-[1.25rem]"
          color="primary"
          component="h3"
          variant="h6"
        >
          {title}
        </Typography>
      </div>
    </div>
  )
}
